import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  FileText, 
  Plus,
  Trash2,
  Edit3,
  Lock,
  Crown
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

// AI Inline Feature Flag Check
import { isFeatureEnabled } from "@/config/features";
import { generateDocumentTitle, extractTitleFromContent } from "@/services/documentTransferService";


import DocumentPreview from "@/components/documents/DocumentPreview";
import DocumentTemplates from "@/components/documents/DocumentTemplates";
import { useDocumentPermissions } from "@/hooks/useDocumentPermissions";
import DocumentCreationInterface from "@/components/documents/DocumentCreationInterface";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SidebarInset } from "@/components/ui/sidebar";
import AllDocumentsModal from "@/components/documents/AllDocumentsModal";

interface Document {
  id: string;
  title: string;
  type: string;
  content: string;
  header_content?: string;
  footer_content?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

const Documents = () => {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentContent, setDocumentContent] = useState("");
  const [headerContent, setHeaderContent] = useState("");
  const [footerContent, setFooterContent] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAIWriting, setIsAIWriting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);
  const [aiContentBuffer, setAiContentBuffer] = useState('');
  const [editorVersion, setEditorVersion] = useState(0);
  const [showAllDocumentsModal, setShowAllDocumentsModal] = useState(false);

  // Flag para controlar se documento foi carregado inicialmente
  const documentLoadedRef = useRef(false);
  const lastAutoSaveRef = useRef<number>(0);

  // Verificar permissões do plano
  const { canCreateDocument, planType, isLoading: permissionsLoading, planStatus } = useDocumentPermissions();

  // Load documents from database
  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]);

  // Evitar recarregamento após IA escrever
  useEffect(() => {
    if (documentId && documents.length > 0 && user && !documentLoadedRef.current) {
      const foundDocument = documents.find(doc => doc.id === documentId);
      if (foundDocument) {
        const shouldLoad = !selectedDocument || selectedDocument.id !== foundDocument.id;
        const hasCurrentContent = documentContent && documentContent.trim().length > 0;
        const isRecentAutoSave = Date.now() - lastAutoSaveRef.current < 5000;
        const isAIGenerating = isAIWriting;
        
        if (shouldLoad && !((hasCurrentContent && isRecentAutoSave) || isAIGenerating)) {
          setSelectedDocument(foundDocument);
          setDocumentContent(foundDocument.content);
          setHeaderContent((foundDocument as any).header_content || foundDocument.title);
          setFooterContent((foundDocument as any).footer_content || `Assinatura<br/>Data: ${new Date().toLocaleDateString('pt-BR')}`);
          documentLoadedRef.current = true;
          console.log('Documents: Documento carregado da URL - ID:', documentId, 'Tipo:', foundDocument.type);
        } else {
          console.log('Documents: Evitando recarregamento - hasContent:', hasCurrentContent, 'recentAutoSave:', isRecentAutoSave, 'isAIWriting:', isAIGenerating);
        }
      } else {
        navigate('/documentos');
        toast({
          title: "Documento não encontrado",
          description: "O documento solicitado não foi encontrado.",
          variant: "destructive",
        });
      }
    } else if (!documentId && selectedDocument) {
      setSelectedDocument(null);
      setDocumentContent("");
      setHeaderContent("");
      setFooterContent("");
      setShowTemplates(false);
      documentLoadedRef.current = false;
    }
  }, [documentId, documents, user, navigate, toast, isAIWriting, documentContent, selectedDocument]);

  // Autosave quando IA termina de escrever
  useEffect(() => {
    if (selectedDocument && documentContent && !isAIWriting) {
      handleSaveDocument();
    }
  }, [isAIWriting]);

  // Reset flag quando muda de documento
  useEffect(() => {
    documentLoadedRef.current = false;
  }, [documentId]);

  // 🚀 LISTENER para aplicar conteúdo gerado pela edge function
  useEffect(() => {
    const handleApplyAIContent = (event: CustomEvent) => {
      console.log('📝 Documents: Evento applyAIContentToEditor capturado:', event.detail);
      const { content, documentId: eventDocId } = event.detail;
      
      // Verificar se é para o documento atual
      if (eventDocId === documentId && content) {
        console.log('✅ Documents: Aplicando conteúdo gerado ao editor...');
        setAiContentBuffer(content);
        setEditorVersion(v => v + 1);
        setIsCreatingDocument(false); // Remove loading overlay
      }
    };

    window.addEventListener('applyAIContentToEditor', handleApplyAIContent as EventListener);
    
    return () => {
      window.removeEventListener('applyAIContentToEditor', handleApplyAIContent as EventListener);
    };
  }, [documentId]);

  const loadDocuments = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar documentos:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os documentos.",
          variant: "destructive",
        });
        return;
      }

      setDocuments(data || []);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    console.log('🔍 DEBUG handleCreateNew - Permissões:', {
      canCreateDocument,
      planType,
      planStatus,
      permissionsLoading,
      userId: user?.id
    });

    if (!canCreateDocument) {
      console.log('❌ BLOQUEIO: canCreateDocument = false');
      toast({
        title: "Criação Bloqueada",
        description: "Upgrade para um plano pago para criar novos documentos.",
        variant: "destructive",
      });
      return;
    }

    console.log('✅ PERMITIDO: Abrindo templates');
    setShowTemplates(true);
  };

  const handleCreateFromText = async (input: string, files?: File[]) => {
    if (!canCreateDocument) {
      toast({
        title: "Criação Bloqueada",
        description: "Upgrade para um plano pago para criar novos documentos.",
        variant: "destructive",
      });
      return;
    }

    if (!user) return;

    try {
      setIsCreatingDocument(true);
      
      const title = input ? generateDocumentTitle(input) : "Novo Documento";
      
      const { data, error } = await supabase
        .from('documents')
        .insert({
          title,
          type: "Gerado por IA",
          content: "",
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar documento:', error);
        setIsCreatingDocument(false);
        toast({
          title: "Erro",
          description: "Não foi possível criar o documento.",
          variant: "destructive",
        });
        return;
      }

      const newDoc = data as Document;
      setDocuments(prev => [newDoc, ...prev]);
      setSelectedDocument(newDoc);
      setDocumentContent("");
      setHeaderContent(newDoc.title);
      setFooterContent(`Assinatura<br/>Data: ${new Date().toLocaleDateString('pt-BR')}`);
      setShowTemplates(false);
      
      navigate(`/documentos/${newDoc.id}`);

      toast({
        title: "Sucesso",
        description: "Documento criado! A IA irá processar sua solicitação.",
      });
      
      if (input.trim()) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('initialDocumentMessage', { 
            detail: { message: input, files, isBlankDocument: true } 
          }));
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao criar documento:', error);
      setIsCreatingDocument(false);
    }
  };

  // Recebe do Context quando a IA terminar
  const onStreamToDocument = useCallback((payload: { mode: string; text: string }) => {
    console.log('📥 Buffer AI recebido:', payload.text.length, 'chars');
    setAiContentBuffer(payload.text || '');
    setEditorVersion(v => v + 1);
  }, []);

  // O EDITOR confirma que aplicou no DOM
  const handleDocumentCreated = useCallback(() => {
    console.log('✅ Editor confirmou aplicação no DOM — removendo loading');
    setIsCreatingDocument(false);
  }, []);

  const handleBackToChat = () => {
    navigate('/chat-juridico');
  };

  const handleContentFromChat = async (content: string, title?: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('documents')
        .insert({
          title: title || `Documento do Chat - ${new Date().toLocaleDateString('pt-BR')}`,
          type: 'Gerado por IA',
          content,
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar documento do chat:', error);
        return null;
      }

      const newDoc = data as Document;
      setDocuments(prev => [newDoc, ...prev]);
      setSelectedDocument(newDoc);
      setDocumentContent(content);
      setShowTemplates(false);
      
      navigate(`/documentos/${newDoc.id}`);

      return newDoc.id;
    } catch (error) {
      console.error('Erro ao criar documento do chat:', error);
      return null;
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatContent = urlParams.get('content');
    const chatTitle = urlParams.get('title');
    
    if (chatContent && user) {
      try {
        const decodedContent = decodeURIComponent(chatContent);
        handleContentFromChat(decodedContent, chatTitle || undefined);
        
        window.history.replaceState({}, '', window.location.pathname);
      } catch (error) {
        console.error('Erro ao processar conteúdo do chat:', error);
      }
    }
  }, [user]);

  const handleBackToDocuments = () => {
    setSelectedDocument(null);
    setDocumentContent("");
    setHeaderContent("");
    setFooterContent("");
    setShowTemplates(false);
    setIsAIWriting(false);
    documentLoadedRef.current = false;
    navigate('/documentos');
  };

  const handleSelectTemplate = async (template: any) => {
    if (!user) return;

    try {
      // 🔧 CORREÇÃO: Remover duplicação de título - pular primeira linha se for igual ao nome do template
      const contentLines = template.content.split('\n');
      const firstLine = contentLines[0]?.trim().toUpperCase();
      const templateNameUpper = template.name.toUpperCase();
      
      // Se primeira linha é duplicação do título, pula ela
      const processableLines = (firstLine === templateNameUpper || firstLine.includes(templateNameUpper)) 
        ? contentLines.slice(1) 
        : contentLines;
      
      // Aplicar formatação ABNT ao conteúdo do template (sem duplicação)
      const formattedContent = `<div class="abnt-format">
        <h1 class="abnt-title" style="text-align: center; font-weight: bold; margin: 1.5em 0; font-size: 14pt;">${template.name}</h1>
        ${processableLines.map((line: string) => 
          line.trim() ? `<p style="text-align: justify; margin: 0.5em 0;">${line}</p>` : '<br>'
        ).join('')}
      </div>`;

      const { data, error } = await supabase
        .from('documents')
        .insert({
          title: template.name,
          type: template.type,
          content: formattedContent,
          user_id: user.id
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar documento:', error);
        toast({
          title: "Erro",
          description: "Não foi possível criar o documento.",
          variant: "destructive",
        });
        return;
      }

      const newDoc = data as Document;
      setDocuments(prev => [newDoc, ...prev]);
      setSelectedDocument(newDoc);
      setDocumentContent(formattedContent);
      // headerContent removido para evitar triplicação
      setFooterContent(`Assinatura<br/>Data: ${new Date().toLocaleDateString('pt-BR')}`);
      setShowTemplates(false);
      
      navigate(`/documentos/${newDoc.id}`);

      toast({
        title: "Sucesso",
        description: "Documento criado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao criar documento:', error);
    }
  };

  const handleSaveDocument = async () => {
    if (!selectedDocument || !user) return;

    try {
      // Extrair título do conteúdo HTML se existir
      const extractedTitle = documentContent ? extractTitleFromContent(documentContent) : '';
      let finalTitle = selectedDocument.title;
      
      if (extractedTitle && extractedTitle !== '' && extractedTitle !== 'Documento') {
        finalTitle = extractedTitle;
        console.log('🔄 Título extraído para salvar:', finalTitle);
      }

      const { data, error } = await supabase
        .from('documents')
        .update({
          title: finalTitle,
          content: documentContent,
          header_content: headerContent,
          footer_content: footerContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedDocument.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao salvar documento:', error);
        toast({
          title: "Erro",
          description: "Não foi possível salvar o documento.",
          variant: "destructive",
        });
        return;
      }

      const updatedDoc = data as Document;
      setDocuments(prev => prev.map(d => d.id === selectedDocument.id ? updatedDoc : d));
      setSelectedDocument(updatedDoc); // Sincronizar título no estado
      
      lastAutoSaveRef.current = Date.now();

      toast({
        title: "Sucesso",
        description: "Documento salvo com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    if (!user) return;

    try {
      const { error } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erro ao excluir documento:', error);
        toast({
          title: "Erro",
          description: "Não foi possível excluir o documento.",
          variant: "destructive",
        });
        return;
      }

      setDocuments(prev => prev.filter(d => d.id !== documentId));
      
      if (selectedDocument?.id === documentId) {
        setSelectedDocument(null);
        setDocumentContent("");
      }

      toast({
        title: "Sucesso",
        description: "Documento excluído com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
    }
  };

  const handleStartRename = (doc: Document, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingDocumentId(doc.id);
    setEditingTitle(doc.title);
  };

  const handleRenameDocument = async (documentId: string) => {
    if (!editingTitle.trim() || !user) {
      setEditingDocumentId(null);
      setEditingTitle("");
      return;
    }

    try {
      const { data, error } = await supabase
        .from('documents')
        .update({
          title: editingTitle.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao renomear documento:', error);
        toast({
          title: "Erro",
          description: "Não foi possível renomear o documento.",
          variant: "destructive",
        });
        return;
      }

      const updatedDoc = data as Document;
      setDocuments(prev => prev.map(d => d.id === documentId ? updatedDoc : d));
      
      if (selectedDocument?.id === documentId) {
        setSelectedDocument(updatedDoc);
      }

      toast({
        title: "Sucesso",
        description: "Documento renomeado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao renomear documento:', error);
    }

    setEditingDocumentId(null);
    setEditingTitle("");
  };

  const handleCancelRename = () => {
    setEditingDocumentId(null);
    setEditingTitle("");
  };

  return (
    <>
      <Helmet>
        <title>Justiçai - Meus Documentos</title>
      </Helmet>
      
      <SidebarInset className="flex min-h-[100svh] w-full flex-col">
          <div className="flex flex-col h-full">
            <div className="flex flex-col h-full">
              <main className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                <div className="flex flex-col gap-8 md:gap-12">
                  {/* Document Creation and Templates Section */}
                  <section id="doc-creation">
                    <div className="documents-container flex flex-1 min-h-0">
                      {/* Main Content Area */}
                      <div className="flex-1 min-h-0">
                        <div className="flex flex-col h-full min-h-0">
                          {/* Chat and Creation Interface */}
                          <div className="documents-main-content flex flex-col h-full min-h-0">
                            {/* Content */}
                            <div className="flex-1 flex min-h-0">
                              {/* Document Creation Interface */}
                              {!selectedDocument && !showTemplates && (
                                <div className="document-creation-container min-h-[360px] sm:min-h-[420px]">
                                  <DocumentCreationInterface 
                                    onCreateDocument={handleCreateFromText}
                                    onShowTemplates={() => setShowTemplates(true)}
                                    disabled={!canCreateDocument}
                                  />
                                </div>
                              )}
                             
                              {/* Templates */}
                              {showTemplates && (
                                <div className="flex-1">
                                  <DocumentTemplates 
                                    onSelectTemplate={handleSelectTemplate}
                                    onBack={() => setShowTemplates(false)}
                                  />
                                </div>
                              )}

                              {/* Document Editor */}
                              {selectedDocument && (
                                <div className="flex-1 overflow-hidden min-h-0">
                                  <div className="h-full overflow-auto">
                                    <DocumentPreview 
                                      document={selectedDocument}
                                      content={documentContent}
                                      onContentChange={setDocumentContent}
                                      headerContent={headerContent}
                                      footerContent={footerContent}
                                      // onHeaderChange removido para evitar triplicação
                                      onFooterChange={setFooterContent}
                                      onBack={handleBackToDocuments}
                                      onSave={handleSaveDocument}
                                      isBlankDocument={!documentContent.trim()}
                                      isAIWriting={isAIWriting}
                                      onAIWritingChange={setIsAIWriting}
                                      isCreatingDocument={isCreatingDocument}
                                      aiContent={aiContentBuffer}
                                      editorVersion={editorVersion}
                                      onDocumentCreated={handleDocumentCreated}
                                      onStreamToDocument={onStreamToDocument}
                                      onTextSelection={(text, position) => {
                                        console.log('Text selected in editor:', text.substring(0, 50) + '...', position);
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Free plan alert - Outside the main content */}
                          {!selectedDocument && !showTemplates && planStatus === 'free' && (
                            <div className="px-4 pb-4">
                              <Alert className="bg-red-50 border-red-200">
                                <Lock className="h-4 w-4 text-red-600" />
                                <AlertDescription className="text-red-800">
                                  <div className="font-medium mb-2">🔒 Plano Gratuito - Criação de Documentos Bloqueada</div>
                                  <div className="text-sm mb-3">
                                    Você pode visualizar documentos existentes, mas precisa de um upgrade para criar novos documentos.
                                  </div>
                                  <Button 
                                    size="sm" 
                                    className="bg-red-600 hover:bg-red-700"
                                    onClick={() => navigate('/planos')}
                                  >
                                    <Crown className="mr-2 h-4 w-4" />
                                    Ver Planos Pagos
                                  </Button>
                                </AlertDescription>
                              </Alert>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Recent Documents Section */}
                  <section id="recent-docs">
                    {!selectedDocument && !showTemplates && documents.length > 0 && (
                      <div className="container mx-auto px-4 py-6">
                        <div className="max-w-4xl mx-auto">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-foreground">Documentos Recentes</h3>
                            <Input
                              placeholder="Pesquisar documentos..."
                              className="w-64"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {documents.filter(doc => {
                              const searchLower = searchTerm.toLowerCase();
                              const titleMatch = doc.title.toLowerCase().includes(searchLower);
                              const typeMatch = doc.type.toLowerCase().includes(searchLower);
                              const dateMatch = new Date(doc.updated_at).toLocaleDateString('pt-BR').includes(searchLower);
                              return titleMatch || typeMatch || dateMatch;
                            }).slice(0, 3).map((doc) => (
                              <Card 
                                key={doc.id} 
                                className="cursor-pointer relative group hover:shadow-md transition-shadow"
                                onClick={() => {
                                  if (editingDocumentId !== doc.id) {
                                    navigate(`/documentos/${doc.id}`);
                                  }
                                }}
                              >
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-base flex items-center gap-2 pr-16">
                                    <FileText className="h-4 w-4" />
                                    {editingDocumentId === doc.id ? (
                                      <Input
                                        value={editingTitle}
                                        onChange={(e) => setEditingTitle(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            handleRenameDocument(doc.id);
                                          }
                                          if (e.key === 'Escape') {
                                            handleCancelRename();
                                          }
                                        }}
                                        onBlur={() => handleRenameDocument(doc.id)}
                                        className="h-6 text-sm"
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    ) : (
                                      doc.title
                                    )}
                                  </CardTitle>
                                  
                                  {/* Action Buttons */}
                                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={(e) => handleStartRename(doc, e)}
                                    >
                                      <Edit3 className="h-3 w-3" />
                                    </Button>
                                    
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Tem certeza que deseja excluir o documento "{doc.title}"? 
                                            Esta ação não pode ser desfeita.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={(e) => handleDeleteDocument(doc.id, e)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Excluir
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </CardHeader>
                                <CardContent>
                                  <p className="text-sm mb-2 text-muted-foreground">
                                    Tipo: {doc.type}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Atualizado em {new Date(doc.updated_at).toLocaleDateString('pt-BR')}
                                  </p>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                          
                          {/* Botão Ver Todos os Documentos */}
                          {documents.length > 3 && (
                            <div className="flex justify-center mt-6">
                              <Button 
                                variant="outline" 
                                onClick={() => setShowAllDocumentsModal(true)}
                                className="px-6"
                              >
                                Ver todos os documentos ({documents.length})
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              </main>
            </div>
          </div>
        </SidebarInset>

        {/* All Documents Modal */}
        <AllDocumentsModal
          isOpen={showAllDocumentsModal}
          onClose={() => setShowAllDocumentsModal(false)}
          onDocumentSelect={(documentId) => {
            navigate(`/documentos/${documentId}`);
          }}
          onDocumentDelete={handleDeleteDocument}
          onDocumentRename={handleStartRename}
          editingDocumentId={editingDocumentId}
          editingTitle={editingTitle}
          setEditingTitle={setEditingTitle}
          onRenameDocument={handleRenameDocument}
          onCancelRename={handleCancelRename}
        />
    </>
  );
};

export default Documents;